import { tradingSummary } from '@/utils/tradingSummary';
import { riskMetrics } from '@/utils/riskStats';
import CountUp from '@/components/base/CountUp';
import Reveal from '@/components/base/Reveal';

interface StatItem {
  label: string;
  value: number;
  decimals: number;
  suffix: string;
  tone?: 'profit' | 'loss';
}

const stats: StatItem[] = [
  { label: 'Total Trades', value: tradingSummary.totalTrades, decimals: 0, suffix: '' },
  { label: 'Winning Trades', value: tradingSummary.winTrades, decimals: 0, suffix: '', tone: 'profit' },
  { label: 'Losing Trades', value: tradingSummary.lossTrades, decimals: 0, suffix: '', tone: 'loss' },
  { label: 'Win Rate', value: tradingSummary.winRateValue, decimals: 1, suffix: '%' },
];

const toneText = (tone?: 'profit' | 'loss'): string => {
  if (tone === 'profit') return 'text-profit-600';
  if (tone === 'loss') return 'text-loss-500';
  return 'text-foreground-950';
};

function JournalStats() {
  return (
    <section className="w-full border-y border-background-200 bg-background-100 px-4 md:px-8 lg:px-12 py-16 md:py-20">
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <p className="text-[11px] font-data tracking-[0.24em] text-primary-600 mb-5">
                THE RECORD IN NUMBERS
              </p>
              <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[30px] leading-[1.08] sm:text-[40px] lg:text-[46px]">
                Every Trade Becomes Data.
              </h2>
            </div>
            <p className="text-[13px] font-data tracking-wide text-foreground-500 lg:pb-2">
              {tradingSummary.period}
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {stats.map((stat, idx) => (
            <Reveal key={stat.label} delay={idx * 90}>
              <div className="rounded-lg border border-background-300 bg-background-50 px-5 py-6 h-full">
                <p
                  className={`font-data text-[40px] md:text-[52px] font-semibold leading-none ${toneText(
                    stat.tone,
                  )}`}
                >
                  <CountUp value={stat.value} decimals={stat.decimals} suffix={stat.suffix} />
                </p>
                <p className="mt-3 text-[13px] text-foreground-500">{stat.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120}>
          <div className="mt-12 flex items-center gap-4">
            <span className="text-[11px] font-data tracking-[0.22em] text-foreground-500 whitespace-nowrap">
              EXTREMES &amp; STREAKS
            </span>
            <span className="h-px flex-1 bg-background-300"></span>
          </div>
        </Reveal>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
          {riskMetrics.map((metric, idx) => (
            <Reveal key={metric.label} delay={idx * 60}>
              <div className="rounded-lg border border-background-300 bg-background-50 px-4 py-4 h-full flex flex-col justify-between gap-3">
                <p
                  className={`font-data text-[20px] md:text-[24px] font-semibold leading-none ${toneText(
                    metric.tone === 'neutral' ? undefined : metric.tone,
                  )}`}
                >
                  {metric.value}
                </p>
                <p className="text-[12px] leading-snug text-foreground-500">{metric.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={160}>
          <p className="mt-8 text-[12px] leading-relaxed text-foreground-400 max-w-[720px]">
            Counts, rates, extremes and streaks are all read straight off the same trade ledger — so
            the record never splits into two disagreeing sets of numbers.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default JournalStats;